import { Flex, Paper, Text } from '@mantine/core';
import { useMemo } from 'react';
import { SiestaStepCounter } from 'src/UI/generate-flow/SiestaStepCounter';
import { SiestaStepCard } from 'src/UI/generate-flow/SiestaStepCard';
import {
  ProcessingSidebarAnimation,
  SummaryItem,
} from 'src/UI/generate-flow/ProcessingSidebarAnimation';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { firmDefaultTemplate } from 'src/data-capture/hooks/templates/defaultTemplate';
import useTemplates from 'src/data-capture/hooks/templates/useTemplates';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { MeetingNoteFlowData, MeetingNoteStepId } from '../types';

type StepDef = {
  id: MeetingNoteStepId;
  title: string;
};

type Props = {
  stepIndex: number;
  steps: StepDef[];
  data: MeetingNoteFlowData;
  currentStepId: MeetingNoteStepId;
  isSubmitting?: boolean;
};

export function MeetingNoteSidebar({
  stepIndex,
  steps,
  data,
  currentStepId,
  isSubmitting = false,
}: Props) {
  const { flagEnabledDefaultTemplates } = useFlags();
  // The flow keeps the template as it was when it was picked, so whether it is the default comes
  // from the current list: it may have been made the default, or replaced as it, since.
  const { files: templates } = useTemplates({ templateType: 'MEETING_NOTE' });
  const firmDefaultId = flagEnabledDefaultTemplates
    ? firmDefaultTemplate(templates)?.id
    : undefined;
  const clientName = getClientName(data);
  const clientId = data.isFamily ? data.family?.mainFamilyMember.clientId : data.client?.id;
  const navClientId = data.isFamily ? data.fileOwnerClientId : data.client?.id;
  const navigateTo =
    data.isFamily && data.family
      ? `/family/${data.family.id}/documents/meeting-notes`
      : `/client/${navClientId}/document/meeting-notes`;

  const hasSelection = useMemo(() => {
    switch (currentStepId) {
      case 'client':
        return data.isFamily ? !!data.family?.id : !!clientId;
      case 'source':
        return !!data.selectedRecordingId;
      case 'structure':
        return !!data.templateType;
      case 'template':
        return !!data.template || !!data.selectedAgendaFileId || !!data.selectedMeetingAgendaId;
      default:
        return false;
    }
  }, [
    currentStepId,
    clientId,
    data.selectedRecordingId,
    data.templateType,
    data.template,
    data.selectedAgendaFileId,
    data.selectedMeetingAgendaId,
  ]);

  if (currentStepId === 'processing') {
    const items: SummaryItem[] = [
      ...(clientName.trim()
        ? [{ id: 'client', label: data.isFamily ? 'Family' : 'Client', value: clientName }]
        : []),
      ...(data.selectedSourceType
        ? [
            {
              id: 'source',
              label: data.selectedSourceType === 'audio' ? 'Recording' : 'Document',
              value:
                data.selectedSourceName ??
                (data.selectedSourceType === 'audio' ? 'Recording' : 'Document'),
            },
          ]
        : []),
      ...(data.template
        ? [
            {
              id: 'template',
              label: 'Template',
              value: data.template.displayName,
              extraLabel: data.template.id === firmDefaultId ? 'Default' : undefined,
            },
          ]
        : []),
      ...(data.selectedAgendaFileName
        ? [{ id: 'agenda', label: 'Agenda', value: data.selectedAgendaFileName }]
        : []),
    ];

    return (
      <ProcessingSidebarAnimation
        items={items}
        clientFirstName={data.isFamily ? data.family?.name : data.client?.firstName}
        documentLabel="Meeting note"
        isSubmitting={isSubmitting}
        navigateTo={navigateTo}
      />
    );
  }

  const showClientPill = stepIndex >= 1 && clientName.trim();
  const showSourcePill = stepIndex >= 2 && data.selectedSourceType;

  return (
    <Flex
      direction="column"
      h="100%"
      pos="relative"
      style={{ transition: 'all 300ms ease-in-out' }}
    >
      {(showClientPill || showSourcePill) && (
        <Flex direction="column" gap="xxs" align="center" style={{ flexShrink: 0 }}>
          {showClientPill && (
            <SummaryPill label={data.isFamily ? 'Family' : 'Client'} value={clientName} />
          )}
          {showSourcePill && (
            <SummaryPill
              label={data.selectedSourceType === 'audio' ? 'Recording' : 'Document'}
              value={
                data.selectedSourceName ??
                (data.selectedSourceType === 'audio' ? 'Recording' : 'Document')
              }
            />
          )}
        </Flex>
      )}

      <Flex direction="column" align="center" justify="center" flex={1} mih={0}>
        <Flex
          direction="column"
          align="center"
          gap="xs"
          style={{
            marginTop:
              showClientPill || showSourcePill ? 'calc(var(--mantine-spacing-xxl) * -1)' : 0,
            transition: 'all 300ms ease-in-out',
          }}
        >
          <SiestaStepCounter currentStep={stepIndex + 1} totalSteps={steps.length - 1} />
          <SiestaStepCard
            steps={steps}
            currentStep={stepIndex + 1}
            dimmed={false}
            minimized={hasSelection}
          />
        </Flex>
      </Flex>
    </Flex>
  );
}

function getClientName(data: MeetingNoteFlowData): string {
  if (data.isFamily) {
    return `${data.family?.name} family`;
  }
  if (data.client) {
    return `${data.client.firstName} ${data.client.lastName}`;
  }
  return '';
}

function SummaryPill({ label, value }: { label: string; value: string }) {
  return (
    <Paper
      radius="xl"
      p="xxs"
      bg="rgba(0, 0, 0, 0.2)"
      w={374}
      style={{
        backdropFilter: 'blur(48px)',
        border: '1px solid rgba(253,253,253,0.15)',
      }}
    >
      <Flex align="center" justify="space-between" px="md" pt="xs">
        <Text size="xs" fw={FONT_WEIGHTS.medium} c="white">
          {label}
        </Text>
      </Flex>
      <Text size="md" fw={FONT_WEIGHTS.bold} c="white" px="md" pb="xs" truncate>
        {value}
      </Text>
    </Paper>
  );
}
